import { io, Socket } from 'socket.io-client';
import { EventBus } from '~/rage/common';
import { sleep } from '~/shared';

export class SocketTransport {
	private readonly BACKEND_URL_WAIT_TIME_MS = 10;
	private readonly MAX_VERBOSE_RECONNECT_ATTEMPTS = 3;
	private readonly ERROR_RECONNECT_TIME_MS = 5_000;

	private backendUrl: string | undefined;
	private token: string | undefined;

	private socket: Socket | null = null;
	private reconnectAttempts = 0;
	private isConnecting: boolean = false;

	private socketReconnectTimeout: ReturnType<typeof setTimeout> | null = null;

	private eventHandlers = new Map<string, Array<(...args: any[]) => unknown>>();

	public setBackendUrl(url: string): void {
		this.backendUrl = url;
	}

	public setToken(token: string): void {
		this.token = token;
	}

	private async getWebSocketUrl(): Promise<string> {
		while (!this.backendUrl) {
			await sleep(this.BACKEND_URL_WAIT_TIME_MS);
		}

		const url = this.backendUrl.replace('http', 'ws') + '/api/client';
		return url;
	}

	public async connect(): Promise<void> {
		if (this.socketReconnectTimeout || this.isConnecting) {
			return;
		}

		this.isConnecting = true;

		try {
			this.disconnect();

			console.log(
				'[SocketService] Connecting to backend at ' + this.backendUrl,
			);

			const wsUrl = await this.getWebSocketUrl();

			await new Promise<void>((resolve) => {
				this.socket = io(wsUrl, {
					auth: (cb) => cb({ token: this.token }),
					transports: ['websocket'],
				});

				let isInitialConnection = true;

				this.socket.on('ready', () => {
					console.log(
						`[SocketService] Rage client socket ${
							isInitialConnection ? '' : 're'
						}connected`,
					);

					isInitialConnection = false;

					this.reconnectAttempts = 0;

					resolve();
				});

				this.socket.on('connect_error', (err) => {
					this.reconnectAttempts++;

					if (this.reconnectAttempts < this.MAX_VERBOSE_RECONNECT_ATTEMPTS) {
						console.error(
							`[SocketService] Rage client socket failed to connect: ${err.message}. Reconnecting...`,
						);
					} else if (
						this.reconnectAttempts === this.MAX_VERBOSE_RECONNECT_ATTEMPTS
					) {
						console.error(
							`[SocketService] Rage client socket failed to connect: ${err.message}. Switching into silent reconnection mode`,
						);
					}

					if (this.socket?.active) {
						return;
					}

					this.socketReconnectTimeout = setTimeout(async () => {
						this.socketReconnectTimeout = null;
						await this.connect();
					}, this.ERROR_RECONNECT_TIME_MS);
				});

				this.socket.on('disconnect', (reason) => {
					console.error(
						`[BackendService] Rage client socket disconnected, reason: ${reason}`,
					);
				});

				this.socket.on('error', (error) => {
					console.error(
						`[BackendService] Socket error for ${this.socket!.id}:`,
						error,
					);
				});

				this.socket.onAny((eventName, payload) => {
					if (!eventName.startsWith('/event/')) {
						return;
					}

					const rageEventName = eventName
						.replace('/event/', '')
						.replace('/', ':');
					console.log('[BackendService] Event:', rageEventName, payload);

					const handlers = this.eventHandlers.get(rageEventName);
					if (handlers) {
						handlers.forEach((handler) => handler(payload));
					}

					EventBus.triggerClient(rageEventName, payload);
				});
			});
		} finally {
			this.isConnecting = false;
		}
	}

	public disconnect(): void {
		if (this.socketReconnectTimeout) {
			clearTimeout(this.socketReconnectTimeout);
			this.socketReconnectTimeout = null;
		}

		if (this.socket?.active) {
			this.socket?.disconnect();
		}
	}

	public async addEventHandler<T = unknown>(
		eventName: string,
		handler: (...args: any[]) => T,
	) {
		let handlers = this.eventHandlers.get(eventName);
		if (!handlers) {
			handlers = [];
			this.eventHandlers.set(eventName, handlers);
		}

		handlers.push(handler);
	}

	// private async getEndpointUrl(methodName: string): Promise<string> {
	// 	while (!this.backendUrl) {
	// 		await sleep(10);
	// 	}

	// 	const url =
	// 		this.backendUrl + '/api/client/rpc/' + methodName.replace(':', '/');
	// 	return url;
	// }

	// public async invoke<P = unknown>(
	// 	methodName: string,
	// 	payload?: P,
	// ): Promise<void> {
	// 	const url = await this.getEndpointUrl(methodName);
	// 	this.socket?.emit('event', url, payload);
	// }
}
